import type { ApiError } from "./api-error";
// Deliberately reaches the LEAF module, not the `@core/toast` barrel: toast/api-error-toast.ts
// imports `../errors`, so errors ↔ toast is a real cycle and the barrel would close it.
// Importing `toast/types` (types only, no runtime deps) breaks it. Do not "fix" this to
// `@core/toast`.
import type { ToastMessage, ToastSeverity } from "../toast/types";

/**
 * Machine-readable `detail` codes emitted by the identity API (api-reference.md). Kept as a
 * hint union for exhaustive comparisons; the wire `code` stays `string` since later modules
 * add their own codes.
 */
export type KnownApiErrorCode =
  | "invalid_credentials"
  | "session_expired"
  | "session_revoked"
  | "account_unavailable"
  | "permission_denied"
  | "verification_invalid"
  | "verification_consumed"
  | "verification_expired"
  | "verification_rate_limited"
  | "invitation_invalid"
  | "invitation_expired"
  | "invitation_not_found"
  | "invitation_not_revocable"
  | "invitation_not_reissuable"
  | "existing_user"
  | "invalid_permission_bundle"
  | "invalid_permission_bundles"
  | "user_not_found"
  | "bundle_not_assigned"
  | "last_administrator_bundle"
  // ACQUIRE round configuration + applications (modules/acquire/api/views.py `error_response`).
  // These arrive in `code`, not `detail`, and several share HTTP 409 — so the code, not the
  // status, is what tells a stale revision apart from a frozen round.
  | "stale_config_revision"
  | "round_configuration_frozen"
  | "round_not_ready"
  | "invalid_general_configuration"
  | "intake_closed"
  | "stale_application_revision"
  | "application_frozen"
  | "application_incomplete"
  | "email_verification_required"
  | "answer_invalid"
  | "document_invalid"
  | "document_limit_reached"
  | "document_upload_incomplete"
  | "application_not_found";

/**
 * The small, UI-facing taxonomy every ApiError collapses into. Kept intentionally coarse —
 * status-driven, with room to refine on `code` (e.g. `account_unavailable`) later.
 */
export type ApiErrorKind =
  | "reauth" // 401 — access/refresh dead; caller must re-authenticate
  | "forbidden" // 403 — authenticated but not allowed (may signal stale bundles)
  | "validation" // 400 — field or shape errors
  | "not_found" // 404
  | "conflict" // 409
  | "rate_limited" // 429
  | "server" // 5xx
  | "network" // request never reached the server
  | "unknown";

/**
 * Collapse an ApiError into its UI-facing kind. Status is authoritative here (the backend
 * has quirks — e.g. DELETE /sessions/{id}/ returns 404 with a permission_denied body — so we
 * classify by status, not string).
 *
 */
export function classifyApiError(error: ApiError): ApiErrorKind {
  const { status } = error;
  if (status === 0) return "network";
  if (status === 401) return "reauth";
  if (status === 403) return "forbidden";
  if (status === 400) return "validation";
  if (status === 404) return "not_found";
  if (status === 409) return "conflict";
  if (status === 429) return "rate_limited";
  if (status >= 500) return "server";
  return "unknown";
}

/**
 * ApiErrorKind → toast severity + i18n message key. Keys live in the "errors.<kind>"
 * namespace (errors.json, French and English).
 */
const TOAST_BY_KIND: Record<
  ApiErrorKind,
  {
    severity: ToastSeverity;
    title: { key: string };
    description: { key: string };
  }
> = {
  reauth: {
    severity: "error",
    title: { key: "errors.reauth.title" },
    description: {
      key: "errors.reauth.description",
    },
  },
  forbidden: {
    severity: "error",
    title: { key: "errors.forbidden.title" },
    description: {
      key: "errors.forbidden.description",
    },
  },
  validation: {
    severity: "warning",
    title: { key: "errors.validation.title" },
    description: {
      key: "errors.validation.description",
    },
  },
  not_found: {
    severity: "error",
    title: { key: "errors.not_found.title" },
    description: {
      key: "errors.not_found.description",
    },
  },
  conflict: {
    severity: "warning",
    title: { key: "errors.conflict.title" },
    description: {
      key: "errors.conflict.description",
    },
  },
  rate_limited: {
    severity: "warning",
    title: { key: "errors.rate_limited.title" },
    description: {
      key: "errors.rate_limited.description",
    },
  },
  server: {
    severity: "error",
    title: { key: "errors.server.title" },
    description: {
      key: "errors.server.description",
    },
  },
  network: {
    severity: "error",
    title: { key: "errors.network.title" },
    description: {
      key: "errors.network.description",
    },
  },
  unknown: {
    severity: "error",
    title: { key: "errors.unknown.title" },
    description: {
      key: "errors.unknown.description",
    },
  },
};

/**
 * i18n key for a machine error code. The French and English copy lives in
 * `core/locales/{fr,en}/errors.json` under `errors.codes.<code>`. Unknown codes
 * fall back to the generic error message at the call site.
 */
export function errorCodeMessageKey(code: string | null): string {
  return code ? `errors.codes.${code}` : "errors.unknown.description";
}

/**
 * Pure mapping: given an ApiError, return the toast severity + title + description to show.
 * Does NOT call the toast (kept pure/testable) — the dispatch helper in core/toast does that.
 */
export function toastForApiError(error: ApiError): {
  severity: ToastSeverity;
  title: ToastMessage;
  description: ToastMessage;
} {
  const mapping = TOAST_BY_KIND[classifyApiError(error)];
  return {
    severity: mapping.severity,
    title: mapping.title,
    description: mapping.description,
  };
}
